import React, { useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useOrderData } from '../../hooks/useOrderData';
import { useCustomerShippingData } from '../../hooks/useCustomerShippingData';

interface CustomerShippingTabProps {
  customerShippingDataHook: ReturnType<typeof useCustomerShippingData>;
}

export const CustomerShippingTab: React.FC<CustomerShippingTabProps> = ({ customerShippingDataHook }) => {
  const toast = useRef<Toast>(null);
  const { data, loading, error, refetch } = customerShippingDataHook;

  // Show toast for errors
  React.useEffect(() => {
    if (error && toast.current) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 5000
      });
    }
  }, [error]);

  const formatCurrency = (amount: number, currencyCode: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('paid') || statusLower.includes('fulfilled')) {
      return 'bg-green-100 text-green-800';
    }
    if (statusLower.includes('pending') || statusLower.includes('partial')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (statusLower.includes('cancelled') || statusLower.includes('refunded')) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="customer-shipping-tab flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="customer-shipping-tab">
        <Toast ref={toast} />
        <div className="text-center p-4">
          <p className="text-600 mb-3">No customer shipping data available</p>
          <Button 
            label="Retry" 
            icon="pi pi-refresh" 
            onClick={refetch}
            className="p-button-outlined"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="customer-shipping-tab">
      <Toast ref={toast} />
      <div className="grid">
        {/* Customer Information */}
        <div className="col-12 md:col-6">
          <Card title="Customer Information">
            <div className="mb-3">
              <strong>Name:</strong> {data.customer_first_name} {data.customer_last_name}
            </div>
            <div className="mb-3">
              <strong>Email:</strong> {data.email}
            </div>
            <div className="mb-3">
              <strong>Phone:</strong> {data.phone || 'N/A'}
            </div>
            <div className="mb-3">
              <strong>Total Spent:</strong> {formatCurrency(data.customer_amount_spent, data.currency_code)}
            </div>
            <div className="mb-3">
              <strong>Total Orders:</strong> {data.customer_no_of_orders}
            </div>
          </Card>
        </div>

        {/* Order Details */}
        <div className="col-12 md:col-6">
          <Card title="Order Details">
            <div className="mb-3">
              <strong>Order ID:</strong> {data.order_id}
            </div>
            <div className="mb-3">
              <strong>Order Name:</strong> {data.name}
            </div>
            <div className="mb-3">
              <strong>Created:</strong> {formatDate(data.created_at)}
            </div>
            <div className="mb-3">
              <strong>Updated:</strong> {formatDate(data.updated_at)}
            </div>
            {data.closed_at && (
              <div className="mb-3">
                <strong>Closed:</strong> {formatDate(data.closed_at)}
              </div>
            )}
            {data.tags && (
              <div className="mb-3">
                <strong>Tags:</strong> 
                <div className="mt-1">
                  {data.tags.split(',').map((tag, index) => (
                    <span key={index} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 border-round text-sm mr-1 mb-1">
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {data.note && (
              <div className="mb-3">
                <strong>Notes:</strong>
                <div className="mt-1 text-600">{data.note}</div>
              </div>
            )}
          </Card>
        </div>

        {/* Financial Information */}
        <div className="col-12 md:col-6">
          <Card title="Financial Information">
            <div className="mb-3">
              <strong>Total Price:</strong> {formatCurrency(data.total_price, data.currency_code)}
            </div>
            <div className="mb-3">
              <strong>Subtotal:</strong> {formatCurrency(data.subtotal_price, data.currency_code)}
            </div>
            <div className="mb-3">
              <strong>Shipping:</strong> {formatCurrency(data.total_shipping_price, data.currency_code)}
            </div>
            <div className="mb-3">
              <strong>Tax:</strong> {formatCurrency(data.total_tax, data.currency_code)}
            </div>
            <div className="mb-3">
              <strong>Discount:</strong> {formatCurrency(data.total_discount, data.currency_code)}
            </div>
            <div className="mb-3">
              <strong>Quantity:</strong> {data.total_quantity}
            </div>
            <div className="mb-3">
              <strong>Financial Status:</strong> 
              <span className={`ml-2 px-2 py-1 border-round text-sm ${getStatusBadgeClass(data.financial_status)}`}>
                {data.financial_status}
              </span>
            </div>
            <div className="mb-3">
              <strong>Payment Gateway:</strong> {data.payment_gateways}
            </div>
            {data.discount_codes && (
              <div className="mb-3">
                <strong>Discount Codes:</strong> {data.discount_codes}
              </div>
            )}
          </Card>
        </div>

        {/* Shipping & Fulfillment */}
        <div className="col-12 md:col-6">
          <Card title="Shipping & Fulfillment">
            <div className="mb-3">
              <strong>Fulfillment Status:</strong>
              <span className={`ml-2 px-2 py-1 border-round text-sm ${getStatusBadgeClass(data.fulfillment_status)}`}>
                {data.fulfillment_status}
              </span>
            </div>
            <div className="mb-3">
              <strong>Return Status:</strong>
              <span className={`ml-2 px-2 py-1 border-round text-sm ${getStatusBadgeClass(data.return_status)}`}>
                {data.return_status}
              </span>
            </div>
            <div className="mb-3">
              <strong>Shipping Address:</strong>
              <div className="mt-1 text-600 white-space-pre-line">
                {data.shipping_address_formatted || 'N/A'}
              </div>
            </div>
            <div className="mb-3">
              <strong>Retail Location:</strong> {data.retail_location_name || 'N/A'}
            </div>
            {data.staff_exists && (
              <div className="mb-3">
                <strong>Staff:</strong> {data.staff_name || 'N/A'}
              </div>
            )}
          </Card>
        </div>

        {/* Billing Information */}
        <div className="col-12">
          <Card title="Billing Information">
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="mb-3">
                  <strong>Billing Address:</strong>
                  <div className="mt-1 text-600 white-space-pre-line">
                    {data.billing_address_formatted || 'N/A'}
                  </div>
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div className="mb-3">
                  <strong>Currency:</strong> {data.currency_code}
                </div>
                <div className="mb-3">
                  <strong>Refundable:</strong> 
                  <span className={`ml-2 px-2 py-1 border-round text-sm ${data.refundable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {data.refundable ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="mb-3">
                  <strong>Payment Status:</strong>
                  <span className="ml-2">
                    {data.fully_paid && <span className="bg-green-100 text-green-800 px-2 py-1 border-round text-sm mr-1">Fully Paid</span>}
                    {data.unpaid && <span className="bg-red-100 text-red-800 px-2 py-1 border-round text-sm mr-1">Unpaid</span>}
                    {!data.fully_paid && !data.unpaid && <span className="bg-yellow-100 text-yellow-800 px-2 py-1 border-round text-sm">Partial</span>}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};